import { useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useRouter } from 'next/router'
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import useAuth from 'hooks/useAuth'
import useLocales from 'hooks/useLocales'
import trackEvent from '~/trackers'
import MyAvatar from 'components/MyAvatar'
import MenuPopover from 'components/MenuPopover'
import InputStyle from 'components/InputStyle'
import Iconify from 'components/Iconify'
import { IconButtonAnimate } from 'components/animate'
import { forceDeleteCookies } from 'helpers/cookie'
import { getErrorCode, getErrorStatus } from 'helpers/error'
import { PATH_AUTH } from 'routes/paths'
import { useActiveOrgStore } from '~/domains/Organization/store'
import useActiveOrg from 'hooks/domains/Organization/useActiveOrg'
import { Organization } from '~/services/organization/interface'
import { useFeatureFlags } from 'helpers/feature-flag'
import { SNACKBAR_ANCHOR } from 'constants/snackbar'
import { FEATURE_FLAGS } from 'constants/feature-flags'

export default function OrgSwitcherPopover() {
  const { t } = useLocales()
  const router = useRouter()
  const { logout, user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const trackerData = { pathname: router.pathname, query: router.query, user }

  const [open, setOpen] = useState<HTMLElement | null>(null)
  const [search, setSearch] = useState('')
  const [switchingId, setSwitchingId] = useState<string | null>(null)

  const { activeOrgId } = useActiveOrgStore(
    useShallow((state) => ({
      activeOrgId: state.activeOrgId,
    })),
  )
  // With the flag off there's nothing to switch between — skip the
  // organization-list fetch entirely rather than show an empty/broken
  // switcher for it.
  const isMultipleRolesEnabled = Boolean(
    useFeatureFlags(FEATURE_FLAGS.multipleRoles),
  )
  const { organizations, isLoading, switchOrg } = useActiveOrg({
    enabled: isMultipleRolesEnabled,
  })

  const handleClose = () => {
    setOpen(null)
    setSearch('')
  }

  const handleSwitch = async (org: Organization) => {
    if (org.org_id === activeOrgId || switchingId) return
    setSwitchingId(org.org_id)
    try {
      await switchOrg(org.org_id)
      handleClose()
      // Reload so every store/query/component-local state resets cleanly
      // under the new org, instead of relying on each one to react to the
      // switch correctly.
      router.reload()
    } catch (error) {
      // A 403 is an answer, not a failure: the Owner has revoked this member's
      // access to that org, so the switch will never succeed. Telling them to
      // "try again in a moment" sends them round a loop that cannot end. Any
      // other status really is transient, and keeps the retry wording.
      const isForbidden =
        getErrorStatus(error as Error) === 403 ||
        getErrorCode(error as Error) === 'FORBIDDEN'
      enqueueSnackbar(
        t(
          isForbidden
            ? 'key_change_organization_forbidden'
            : 'key_change_organization_error',
        ),
        {
          variant: 'error',
          anchorOrigin: SNACKBAR_ANCHOR,
        },
      )
    } finally {
      setSwitchingId(null)
    }
  }

  const handleLogout = async () => {
    trackEvent.user('trigger_logout', trackerData, {
      modal_name: 'AVATAR_DROP_DOWN',
    })
    try {
      // logout() clears the active org + cached /me data itself now
      // (AuthContext.resetDataAfterLogout), so the next login in this
      // browser doesn't inherit either.
      await logout()
      forceDeleteCookies()
      router.push(PATH_AUTH.login)
    } catch (error) {
      console.error(error)
      enqueueSnackbar(t('key_logout_error'), {
        variant: 'error',
        anchorOrigin: SNACKBAR_ANCHOR,
      })
    }
  }

  // The trigger shows the organization you're acting as, not the signed-in
  // user — that's what the switcher switches. Falls back to the user's avatar
  // until GET /organizations resolves and an active org is known.
  const activeOrg = useMemo(
    () => organizations.find((org) => org.org_id === activeOrgId),
    [organizations, activeOrgId],
  )

  const filteredOrgs = useMemo(() => {
    const query = search.trim().toLowerCase()
    return organizations.filter((org) =>
      (org.org_name || '').toLowerCase().includes(query),
    )
  }, [organizations, search])

  return (
    <>
      <IconButtonAnimate
        id="b-org-switcher-trigger"
        onClick={(event) => {
          trackEvent.user('trigger_avatar_drop_down', trackerData)
          setOpen(event.currentTarget)
        }}
        sx={{ p: 0, borderRadius: 1 }}
      >
        {activeOrg ? (
          <Avatar
            src={activeOrg.org_avatar || undefined}
            alt={activeOrg.org_name}
            sx={{ width: 40, height: 40 }}
          >
            {(activeOrg.org_name || '?').charAt(0)}
          </Avatar>
        ) : (
          <MyAvatar />
        )}
        <Iconify
          icon="eva:chevron-down-fill"
          sx={{ width: 20, height: 20, ml: 0.5, color: 'text.secondary' }}
        />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ p: 2, mt: 1.5, ml: 0.75, width: 320 }}
      >
        {isMultipleRolesEnabled ? (
          <>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {t('key_select_organization')}
            </Typography>

            <InputStyle
              id="i-org-search-organization"
              size="small"
              fullWidth
              placeholder={t('key_search_organization')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:search-fill"
                      sx={{ width: 20, height: 20, color: 'text.disabled' }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ maxHeight: 320, overflowY: 'auto', my: 1.5 }}>
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!isLoading && filteredOrgs.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}
                >
                  {t('key_no_organizations_found')}
                </Typography>
              )}

              {!isLoading &&
                filteredOrgs.map((org) => {
                  const isActive = org.org_id === activeOrgId
                  return (
                    <Stack
                      key={org.org_id}
                      id={`b-org-switch-${org.org_id}`}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      onClick={() => handleSwitch(org)}
                      sx={{
                        px: 1,
                        py: 1,
                        borderRadius: 1,
                        cursor: isActive ? 'default' : 'pointer',
                        '&:hover': { bgcolor: isActive ? 'transparent' : 'action.hover' },
                      }}
                    >
                      <Avatar
                        src={org.org_avatar || undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {(org.org_name || '?').charAt(0)}
                      </Avatar>
                      <Typography
                        variant="subtitle2"
                        sx={{ flexGrow: 1 }}
                        noWrap
                      >
                        {org.org_name}
                      </Typography>
                      {switchingId === org.org_id ? (
                        <CircularProgress size={16} />
                      ) : isActive ? (
                        <Iconify
                          icon="eva:checkmark-fill"
                          sx={{ width: 20, height: 20, color: 'primary.main' }}
                        />
                      ) : (
                        <Iconify
                          icon="eva:chevron-right-fill"
                          sx={{ width: 20, height: 20, color: 'text.disabled' }}
                        />
                      )}
                    </Stack>
                  )
                })}
            </Box>

            <Divider sx={{ mb: 1 }} />
          </>
        ) : (
          <Typography variant="subtitle2" sx={{ mb: 1.5 }} noWrap>
            {user?.displayName}
          </Typography>
        )}

        <Button
          id="b-org-logout"
          fullWidth
          variant="text"
          onClick={handleLogout}
          sx={{ fontWeight: 'bold' }}
        >
          {t('key_logout_this_account')}
        </Button>
      </MenuPopover>
    </>
  )
}
